import {
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  computed,
  forwardRef,
  inject
} from '@angular/core';
import { UmssIconComponent } from '../icon/icon.component';
import {
  dialogEnterLeave,
  overlayFadeAnimation,
  overlayHostLeaveAnimation
} from '../../animations';
import {
  UMSS_CLOSE_OVERLAY,
  UmssOverlayBaseDirective
} from '../overlay-base/overlay-base.directive';
import { BreakpointService } from '../../services/breakpoint.service';
import { OverlayZIndexService } from '../../services/overlay-z-index.service';

@Component({
  selector: 'umss-dialog',
  standalone: true,
  imports: [UmssIconComponent],
  templateUrl: './dialog.component.html',
  styleUrls: ['./dialog.component.scss'],
  animations: [
    overlayFadeAnimation,
    dialogEnterLeave,
    overlayHostLeaveAnimation
  ],
  providers: [
    {
      provide: UMSS_CLOSE_OVERLAY,
      useExisting: forwardRef(() => UmssDialogComponent)
    }
  ]
})
export class UmssDialogComponent
  extends UmssOverlayBaseDirective
  implements OnChanges
{
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly breakpointService = inject(BreakpointService);
  private readonly zIndexService = inject(OverlayZIndexService);

  protected overlayZIndex = this.zIndexService.base;

  /** Variant props - Well-typed and comprehensive */
  @Input() size: 'Small' | 'Medium' | 'Large' | 'Full' = 'Medium';
  @Input() type: 'Modal' | 'BottomSheet' | 'Responsive' = 'Modal';
  @Input() position: 'Center' | 'Top' | 'Bottom' = 'Center';

  /** Whether to show the drag handle bar (only applies to BottomSheet type) */
  @Input() showHandle: boolean = true;

  /**
   * Resolves the effective presentation type.
   * When `type` is 'Responsive', automatically uses 'BottomSheet' on mobile
   * and 'Modal' on larger screens, reacting to viewport changes.
   */
  readonly effectiveType = computed<'Modal' | 'BottomSheet'>(() =>
    this.type === 'Responsive'
      ? this.breakpointService.isMobile()
        ? 'BottomSheet'
        : 'Modal'
      : this.type
  );

  ngOnChanges(changes: SimpleChanges): void {
    const openChange = changes['open'];
    if (openChange?.currentValue === true && !openChange.previousValue) {
      this.overlayZIndex = this.zIndexService.next();
    }

    if (changes['type'] && !changes['type'].firstChange && this._visible) {
      this._visible = false;
      this.cdr.detectChanges();
      this._visible = true;
    }
  }

  get dialogAnimationParams() {
    if (this.effectiveType() === 'BottomSheet') {
      return {
        value: true,
        params: {
          enterFrom: 'translateY(100%)',
          leaveTo: 'translateY(100%)',
          enterEasing: 'cubic-bezier(0.35, 0, 0.25, 1)',
          leaveEasing: 'cubic-bezier(0.35, 0, 0.25, 1)',
          enterFadeDuration: 150,
          enterSlideDuration: 320,
          leaveFadeDuration: 280,
          leaveSlideDuration: 280,
          leaveOpacity: 1
        }
      };
    }
    // Modal (Center / Top / Bottom)
    const modalTransforms: Record<
      string,
      { enterFrom: string; leaveTo: string; enterEasing: string }
    > = {
      Center: {
        enterFrom: 'scale(0.95) translateY(10px)',
        leaveTo: 'scale(0.95) translateY(6px)',
        enterEasing: 'cubic-bezier(0.34, 1.56, 0.64, 1)'
      },
      Top: {
        enterFrom: 'translateY(-16px)',
        leaveTo: 'translateY(-12px)',
        enterEasing: 'cubic-bezier(0.34, 1.56, 0.64, 1)'
      },
      Bottom: {
        enterFrom: 'translateY(16px)',
        leaveTo: 'translateY(12px)',
        enterEasing: 'cubic-bezier(0.34, 1.56, 0.64, 1)'
      }
    };
    const params = modalTransforms[this.position] ?? modalTransforms['Center'];
    return { value: true, params };
  }
}
